import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
  type ISourceEntity,
} from '@principle-theorem/practice-migrations/interfaces';
import { Timestamp, Timezone, TypeGuard } from '@principle-theorem/shared';
import { flow, groupBy, isBoolean, isNull, isNumber, isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import { OFFSET_PLACEHOLDER } from '../../../source/source-helpers';
import { convertExactId, getExactSourceDate } from '../../util/helpers';
import { PatientSourceEntity } from './patient';

export const PATIENT_MEDICAL_HISTORIES_RESOURCE_TYPE =
  'patientMedicalHistories';

export const PATIENT_MEDICAL_HISTORIES_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Patient Medical Histories List',
      description: '',
      idPrefix: PATIENT_MEDICAL_HISTORIES_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

enum ExactMedicalHistoryRecordType {
  Question = 'Question',
}

export enum ExactMedicalHistoryQuestionType {
  Bool = 'Bool',
  Text = 'Text',
}

interface IExactMedicalHistoryQuestionResult
  extends IExactMedicalHistoryQuestion {
  id: string;
  patient_id: string;
  history_date: string;
  user_id: string | null;
}

export interface IExactMedicalHistoryQuestion {
  question_type: string | null;
  question_text: string;
  answer_text: string | null;
  answer_is_yes: boolean | null;
  question_order: number;
}

export interface IExactPatientMedicalHistory {
  id: string;
  patient_id: string;
  history_date: string;
  user_id: string | null;
  questions: IExactMedicalHistoryQuestion[];
}

export function isExactPatientMedicalHistory(
  item: unknown,
  logError: boolean
): item is IExactPatientMedicalHistory {
  return TypeGuard.interface<IExactPatientMedicalHistory>(
    {
      id: isString,
      patient_id: isString,
      history_date: isString,
      user_id: [isString, isNull],
      questions: TypeGuard.arrayOf(isExactMedicalHistoryQuestion),
    },
    logError
  )(item);
}

function isExactMedicalHistoryQuestion(
  item: unknown
): item is IExactMedicalHistoryQuestion {
  return TypeGuard.interface<IExactMedicalHistoryQuestion>(
    {
      question_type: [isString, isNull],
      question_text: isString,
      answer_text: [isString, isNull],
      answer_is_yes: [isBoolean, isNull],
      question_order: isNumber,
    },
    true
  )(item);
}

export interface IExactMedicalHistoryTranslations {
  historyDate: Timestamp;
}

export interface IExactMedicalHistoryFilters {
  patientId: string;
  historyDate: Timestamp;
}

const MEDICAL_HISTORY_ESTIMATE_QUERY = `
SELECT convmedicalhistory_history_sourceid
FROM convmedicalhistory
WHERE recordtype = '${ExactMedicalHistoryRecordType.Question}'
GROUP BY convmedicalhistory_history_sourceid
`;

const MEDICAL_HISTORY_SOURCE_QUERY = `
WITH limited_history AS (
  SELECT convmedicalhistory_history_sourceid
  FROM convmedicalhistory
  WHERE recordtype = '${ExactMedicalHistoryRecordType.Question}'
  GROUP BY convmedicalhistory_history_sourceid
  ORDER BY convert_to_integer(REGEXP_REPLACE(convmedicalhistory_history_sourceid, ':.*$', ''))
  ${OFFSET_PLACEHOLDER}
)
SELECT
  question.convmedicalhistory_history_sourceid::TEXT AS id,
  question.convmedicalhistory_history_patientid::TEXT AS patient_id,
  question.convmedicalhistory_history_date AS history_date,
  NULLIF(question.convmedicalhistory_history_user, '') AS user_id,
  NULLIF(question.question_question_questiontype, '') AS question_type,
  question.question_question_text AS question_text,
  NULLIF(question.answer_answer_text, '') AS answer_text,
  question.answer_answer_isyes AS answer_is_yes,
  question.question_question_order::INTEGER AS question_order
FROM
  limited_history history
INNER JOIN
  convmedicalhistory question
ON
  history.convmedicalhistory_history_sourceid = question.convmedicalhistory_history_sourceid
  AND question.recordtype = '${ExactMedicalHistoryRecordType.Question}'
ORDER BY
  convert_to_integer(REGEXP_REPLACE(history.convmedicalhistory_history_sourceid, ':.*$', '')),
  question.question_question_order
`;

export type ExactPatientMedicalHistory = ISourceEntityTypes<
  IExactPatientMedicalHistory,
  IExactMedicalHistoryTranslations,
  IExactMedicalHistoryFilters
>;

export class PatientMedicalHistoriesSourceEntity extends BaseSourceEntity<ExactPatientMedicalHistory> {
  sourceEntity = PATIENT_MEDICAL_HISTORIES_SOURCE_ENTITY;
  entityResourceType = PATIENT_MEDICAL_HISTORIES_RESOURCE_TYPE;
  sourceQuery = MEDICAL_HISTORY_SOURCE_QUERY;
  allowOffsetJob = true;
  verifySourceFn = isExactPatientMedicalHistory;
  override defaultOffsetSize = 5000;
  override estimateQuery = MEDICAL_HISTORY_ESTIMATE_QUERY;

  override requiredEntities = {
    patients: new PatientSourceEntity(),
  };

  override transformDataFn = flow([transformMedicalHistoryResults]);

  getSourceRecordId(data: IExactPatientMedicalHistory): string {
    return `${data.patient_id} ${data.id}`;
  }

  getSourceLabel(data: IExactPatientMedicalHistory): string {
    return `${data.patient_id} ${data.id}`;
  }

  translate(
    data: IExactPatientMedicalHistory,
    timezone: Timezone
  ): IExactMedicalHistoryTranslations {
    return {
      historyDate: getExactSourceDate(data.history_date, timezone),
    };
  }

  getFilterData(
    data: IExactPatientMedicalHistory,
    timezone: Timezone
  ): IExactMedicalHistoryFilters {
    return {
      patientId: data.patient_id,
      historyDate: getExactSourceDate(data.history_date, timezone),
    };
  }
}

function transformMedicalHistoryResults(
  rows: IExactMedicalHistoryQuestionResult[]
): IExactPatientMedicalHistory[] {
  const historyGroups = groupBy(rows, (row) => row.id);

  return Object.values(historyGroups).map((historyGroup) => {
    const [header] = historyGroup;
    return {
      id: convertExactId(header.id),
      patient_id: convertExactId(header.patient_id),
      history_date: header.history_date,
      user_id: header.user_id,
      questions: historyGroup.map((row) => ({
        question_type: row.question_type,
        question_text: row.question_text,
        answer_text: row.answer_text,
        answer_is_yes: row.answer_is_yes,
        question_order: row.question_order,
      })),
    };
  });
}
